import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { FC, useMemo } from 'react';
import type { Key, HTMLAttributes } from 'react';
import { Autocomplete, InputAdornment } from '@mui/material';
import { CountryWithFlag } from 'ui/CountryWithFlag';
import { CountryFlag } from 'ui/CountryFlag';
import { getAllCountries } from 'redux/reducers/settings/selectors';
import { useSelector } from 'react-redux';
// import countries from './countries-world.json';
import { TCountrySelectProps } from './types';
import { ECountryType } from 'ui/EasTable/types';
import { ETableStructureType } from 'services/constants/tableStructureTypes';

export const CountrySelectMenu: FC<TCountrySelectProps> = ({
	onChange,
	onChangeNumber,
	countryList,
	countryCode,
	countryNumber,
	label = 'Country',
	size = 'small',
	type = ETableStructureType.COUNTRY_CODE,
	typeOfCountry,
	disabled = false,
	required = false,
}) => {
	const countries = useSelector(getAllCountries);
	const allCountryList = useMemo(
		() => countries?.map((item) => ({ code: item.isoAlpha2, label: item.name, number: item.id })),
		[countries],
	);
	const euCountryList = useMemo(
		() =>
			countries
				?.filter((item) => item.isEu)
				.map((item) => ({ code: item.isoAlpha2, label: item.name, number: item.id })),
		[countries],
	);
	const nonEuCountryList = useMemo(
		() =>
			countries
				?.filter((item) => !item.isEu && item.isSupported)
				.map((item) => ({ code: item.isoAlpha2, label: item.name, number: item.id })),
		[countries],
	);
	const supportedCountryList = useMemo(
		() =>
			countries
				?.filter((item) => item.isSupported)
				.map((item) => ({ code: item.isoAlpha2, label: item.name, number: item.id })),
		[countries],
	);
	const computedCountryList = useMemo(
		() =>
			countryList ??
			(typeOfCountry === ECountryType.EU
				? (euCountryList ?? [])
				: typeOfCountry === ECountryType.NON_EU
					? (nonEuCountryList ?? [])
					: typeOfCountry === ECountryType.SUPPORTED
						? (supportedCountryList ?? [])
						: (allCountryList ?? [])),
		[
			countryList,
			typeOfCountry,
			euCountryList,
			allCountryList,
			nonEuCountryList,
			supportedCountryList,
		],
	);

	const country = computedCountryList.find((item) => item.code === countryCode) ?? {
		code: '',
		label: '',
		number: 0,
	};
	const countryNmbr = computedCountryList.find(
		(item) => (item?.number ?? 0) === (countryNumber ?? 0),
	) ?? {
		code: '',
		label: '',
		number: 0,
	};

	return (
		<Box
			sx={{ width: '100%', m: '8px 0' }}
			onKeyDown={(event) => {
				// MenuList type-ahead reacts to printable keys and moves focus off the input,
				// which closes the options popup. Escape/Tab must still reach the Menu.
				if (event.key !== 'Escape' && event.key !== 'Tab') {
					event.stopPropagation();
				}
			}}
		>
			<Autocomplete
				disabled={disabled}
				sx={{
					width: '100%',
					'& .MuiInputBase-root': { width: '100%', borderRadius: '12px' },
				}}
				size={size}
				options={computedCountryList}
				onChange={(event, newValue) => {
					if (newValue) {
						if (type === ETableStructureType.COUNTRY_CODE) {
							onChange?.(newValue.code);
						} else {
							console.log('Autocomplete - newValue', newValue, newValue?.number ?? 0);
							onChangeNumber?.(newValue?.number ?? 0);
						}
					}
				}}
				value={type === ETableStructureType.COUNTRY_CODE ? country : countryNmbr}
				autoHighlight
				getOptionLabel={(option) => option.label}
				renderOption={(props, option) => {
					const { key, ...optionProps } = props as unknown as {
						key?: Key;
					} & HTMLAttributes<HTMLLIElement>;
					return (
						<Box component="li" key={key} {...optionProps}>
							<Box
								sx={{
									width: '100%',
									display: 'flex',
									// flexDirection: 'row',
									justifyContent: 'space-between',
									alignItems: 'center',

									'& > div .fi': { mr: 2, flexShrink: 0 },
								}}
							>
								<Box>
									<CountryWithFlag countryCode={option.code} />
								</Box>
								<Box>{option.code}</Box>
							</Box>
						</Box>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						required={required}
						placeholder="Choose a country"
						inputProps={{
							...params.inputProps,
							autoComplete: 'new-password',
							startadornment: (
								<InputAdornment position="start">
									<CountryFlag countryCode={String(params?.inputProps?.value ?? '')} height={15} />
								</InputAdornment>
							),
						}}
					/>
				)}
			/>
		</Box>
	);
};
